import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TablePagination,
  Skeleton,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  PAGAMENTOS_INSCRICAO_FILTRADOS,
  type PagamentosInscricaoFiltradosData,
  type PagamentosInscricaoFiltradosVars,
  type OrdenacaoPagamento,
  type DirecaoOrdenacao,
} from '../../../services/pagamentoInscricaoService';
import { StatusPagamento } from '../../../types/pagamento';
import PaymentStatusChip from '../../../components/PaymentStatusChip';
import { IsentoBadge, OrigemConfirmacaoBadge } from '../../../components/financeiro';
import { formatCurrency } from '../../../utils/money';
import type { CategoriaTorneio } from '../../../services/torneioService';
import { useDebounce } from '../../../hooks/useDebounce';

const COLUMN_COUNT = 7;

function possuiReembolsoAtivo(pedidosReembolso?: { id: string; status: string }[]): boolean {
  return !!pedidosReembolso?.some((p) => p.status !== 'RECUSADO' && p.status !== 'FINALIZADO');
}

const STATUS_OPTIONS: { label: string; value: StatusPagamento | null }[] = [
  { label: 'Todos', value: null },
  { label: 'Pago', value: 'PAGO' as StatusPagamento },
  { label: 'Pendente', value: 'PENDENTE' as StatusPagamento },
  { label: 'Cancelado', value: 'CANCELADO' as StatusPagamento },
  { label: 'Estornado', value: 'ESTORNADO' as StatusPagamento },
  { label: 'Gratuito', value: 'GRATUITO' as StatusPagamento },
];

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

interface TournamentFinanceiroRecebimentosTabProps {
  torneioId: string;
  categorias: CategoriaTorneio[];
}

const TournamentFinanceiroRecebimentosTab: React.FC<TournamentFinanceiroRecebimentosTabProps> = ({
  torneioId,
  categorias,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<StatusPagamento | null>(null);
  const [categoriaFilter, setCategoriaFilter] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string | null>(null);
  const [dataFim, setDataFim] = useState<string | null>(null);
  const [nomeAtleta, setNomeAtleta] = useState('');
  const [sortBy, setSortBy] = useState<OrdenacaoPagamento | null>(null);
  const [direction, setDirection] = useState<DirecaoOrdenacao>('DESC');

  // Cada mudança de filtro dispara NOVA CONSULTA ao servidor (variáveis do useQuery) -- nenhuma
  // lista é filtrada em memória sobre a página já carregada (AC-A4).
  const debouncedNomeAtleta = useDebounce(nomeAtleta, 300);

  const hasDateError = !!(
    dataInicio &&
    dataFim &&
    dayjs(dataInicio).isAfter(dayjs(dataFim))
  );

  const { data, loading, error } = useQuery<
    PagamentosInscricaoFiltradosData,
    PagamentosInscricaoFiltradosVars
  >(PAGAMENTOS_INSCRICAO_FILTRADOS, {
    variables: {
      torneioId,
      status: statusFilter || undefined,
      categoriaTorneioId: categoriaFilter || undefined,
      nomeAtleta: debouncedNomeAtleta.trim() || undefined,
      sortBy: sortBy || undefined,
      direction: sortBy ? direction : undefined,
      page: 0,
      size: 9999,
    },
    skip: !torneioId,
    fetchPolicy: 'no-cache',
  });

// A aba Recebimentos excluía `GRATUITO` da listagem desde 2026-03-10 (commit
// 0b3d085), sob a regra "recebimentos é dinheiro que entrou" — regra que a PRD
// `correcoes-reuniao-05082026` avaliou e manteve. O owner a reverteu em QA
// (2026-09-01), pela alternativa que a própria PRD havia listado e descartado:
// incluir o gratuito, porém MARCADO como isento (ver `IsentoBadge`), para que
// uma linha de R$ 0,00 não se confunda com valor recebido.
  const allItems = useMemo(
    () => data?.pagamentosInscricaoFiltrados?.items ?? [],
    [data],
  );

  const categoriaOptions = useMemo(
    () =>
      categorias
        .map((cat) => ({ id: cat.id, nome: cat.nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome)),
    [categorias],
  );

  // Data ainda é aplicada no cliente sobre o resultado do servidor -- `pagamentosInscricaoFiltrados`
  // não recorta por período nesta suíte, e o filtro de data não faz parte das 3 capacidades
  // migradas (AC-A4); mantido pelo mesmo motivo do comportamento anterior.
  const filteredItems = useMemo(() => {
    let items = allItems;

    if (dataInicio && !hasDateError) {
      const start = dayjs(dataInicio).startOf('day');
      items = items.filter((p) => {
        const date = p.dataPagamento || p.criadoEm;
        return date ? dayjs(date).isAfter(start) || dayjs(date).isSame(start) : false;
      });
    }

    if (dataFim && !hasDateError) {
      const end = dayjs(dataFim).endOf('day');
      items = items.filter((p) => {
        const date = p.dataPagamento || p.criadoEm;
        return date ? dayjs(date).isBefore(end) || dayjs(date).isSame(end) : false;
      });
    }

    return items;
  }, [allItems, dataInicio, dataFim, hasDateError]);

  const paginatedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setStatusFilter(null);
    setCategoriaFilter(null);
    setDataInicio(null);
    setDataFim(null);
    setNomeAtleta('');
    setSortBy(null);
    setDirection('DESC');
    setPage(0);
  };

  const handleSort = (field: OrdenacaoPagamento) => {
    if (sortBy === field) {
      setDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(field);
      setDirection('ASC');
    }
    setPage(0);
  };

  const linkStyle = { color: '#1976d2', textDecoration: 'none' as const };

  const renderCategoriaLinks = (
    inscricoes?: {
      categoriaTorneio?: {
        id: string;
        nome: string;
        torneio?: { id: string } | null;
      } | null;
    }[],
  ) => {
    if (!inscricoes || inscricoes.length === 0) return '-';
    const seen = new Map<string, { catId: string; catNome: string; torneioId: string }>();
    inscricoes.forEach((ins) => {
      const cat = ins.categoriaTorneio;
      if (cat?.id && cat.nome && cat.torneio?.id) {
        seen.set(cat.id, { catId: cat.id, catNome: cat.nome, torneioId: cat.torneio.id });
      }
    });
    const entries = Array.from(seen.values());
    if (entries.length === 0) return '-';
    return entries.map((e, i) => (
      <React.Fragment key={e.catId}>
        {i > 0 && ', '}
        <Link to={`/tournament/${e.torneioId}/category/${e.catId}`} style={linkStyle}>
          {e.catNome}
        </Link>
      </React.Fragment>
    ));
  };

  const renderUsuarioLinks = (
    inscricoes?: {
      equipe?: {
        atletas?: { id: string; usuario?: { nome: string } | null }[];
      } | null;
    }[],
  ) => {
    if (!inscricoes || inscricoes.length === 0) return '-';
    const seen = new Map<string, { atletaId: string; nome: string }>();
    inscricoes.forEach((ins) => {
      ins.equipe?.atletas?.forEach((a) => {
        if (a.usuario?.nome) {
          seen.set(a.id, { atletaId: a.id, nome: a.usuario.nome });
        }
      });
    });
    const entries = Array.from(seen.values());
    if (entries.length === 0) return '-';
    return entries.map((e, i) => (
      <React.Fragment key={e.atletaId}>
        {i > 0 && ', '}
        <Link to={`/userpage/${e.atletaId}`} style={linkStyle}>
          {e.nome}
        </Link>
      </React.Fragment>
    ));
  };

  return (
    <>
      {/* Filter Bar */}
      <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
            <TextField
              label="Nome do Atleta"
              size="small"
              fullWidth
              value={nomeAtleta}
              onChange={(e) => {
                setNomeAtleta(e.target.value);
                setPage(0);
              }}
              placeholder="Buscar por nome"
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Categoria"
              size="small"
              fullWidth
              value={categoriaFilter || ''}
              onChange={(e) => {
                setCategoriaFilter(e.target.value || null);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Todas</MenuItem>
              {categoriaOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nome}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={statusFilter || ''}
              onChange={(e) => {
                setStatusFilter((e.target.value || null) as StatusPagamento | null);
                setPage(0);
              }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value || ''}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Início"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataInicio || ''}
              onChange={(e) => {
                setDataInicio(e.target.value || null);
                setPage(0);
              }}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : undefined}
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={dataFim || ''}
              onChange={(e) => {
                setDataFim(e.target.value || null);
                setPage(0);
              }}
              error={hasDateError}
            />
          </Box>
          <Box sx={{ flex: '0 1 auto' }}>
            <Button variant="outlined" color="inherit" size="small" onClick={handleResetFilters}>
              Limpar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Usuário</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortBy === 'VALOR'}
                  direction={sortBy === 'VALOR' ? direction.toLowerCase() as 'asc' | 'desc' : 'asc'}
                  onClick={() => handleSort('VALOR')}
                  style={{ color: 'inherit' }}
                >
                  Valor
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortBy === 'STATUS'}
                  direction={sortBy === 'STATUS' ? direction.toLowerCase() as 'asc' | 'desc' : 'asc'}
                  onClick={() => handleSort('STATUS')}
                  style={{ color: 'inherit' }}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Origem</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Meio Pagamento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortBy === 'DATA'}
                  direction={sortBy === 'DATA' ? direction.toLowerCase() as 'asc' | 'desc' : 'asc'}
                  onClick={() => handleSort('DATA')}
                  style={{ color: 'inherit' }}
                >
                  Data
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cidx) => (
                    <TableCell key={cidx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="alert alert-danger mb-0" role="alert">
                    Erro ao carregar recebimentos. Tente novamente.
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  Nenhum recebimento encontrado.
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{renderUsuarioLinks(p.inscricoes)}</TableCell>
                  <TableCell>{renderCategoriaLinks(p.inscricoes)}</TableCell>
                  <TableCell>{formatCurrency(p.valor ?? 0)}</TableCell>
                  <TableCell>
                    <PaymentStatusChip
                      status={possuiReembolsoAtivo(p.pedidosReembolso) ? 'PEDIDO_REEMBOLSO' : p.status}
                    />
                    <IsentoBadge inscricoes={p.inscricoes} />
                  </TableCell>
                  <TableCell>
                    <OrigemConfirmacaoBadge status={p.origemConfirmacao} />
                  </TableCell>
                  <TableCell>{p.meioPagamento || '-'}</TableCell>
                  <TableCell>{formatDate(p.dataPagamento || p.criadoEm)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </TableContainer>
    </>
  );
};

export default TournamentFinanceiroRecebimentosTab;
